/**
 * Archivo de resultados: todo lo jugado, del más nuevo al más viejo.
 *
 * Agrupado por FECHA local del que mira, que es como se lee un archivo
 * deportivo ("sábado 15", "domingo 16") y no como viene del server (una lista
 * plana ordenada por timestamp). El agrupado va acá y no en el API por lo
 * mismo que el "hoy" de la portada: el día depende del huso horario del
 * espectador y el snapshot del server es uno solo para todos (ADR-020).
 *
 * Hay un filtro por competencia porque una federación corre varias a la vez y
 * el que entra viene por una: es un `<select>` y no una pantalla nueva porque
 * el documento ya está bajado entero.
 */
import { useState } from 'react';
import { getLocale, LOCALE_TAGS } from '../../i18n/index';
import { useT } from '../../i18n/use-t';
import type { PublicClient, PublicFixtureEntry, PublicResultsResponse } from '../../lib/public-client';
import { usePolled } from './hooks';
import { EmptyBlock, MatchCard, Section } from './parts';
import { PublicState, Updated } from './PublicState';

/** Clave de agrupación: el día LOCAL del espectador, no el UTC del server. */
function localDayKey(iso: string | null): string {
  if (iso === null) return '';
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return '';
  return `${at.getFullYear()}-${at.getMonth() + 1}-${at.getDate()}`;
}

function localDayLabel(iso: string | null, fallback: string): string {
  if (iso === null) return fallback;
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return fallback;
  return at.toLocaleDateString(LOCALE_TAGS[getLocale()], {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

export function PublicResults({
  client,
  slug,
  base,
  onNavigate,
}: {
  client: PublicClient;
  slug: string;
  base: string;
  onNavigate: (href: string) => void;
}): JSX.Element {
  const t = useT();
  const state = usePolled<PublicResultsResponse>(() => client.getResults(slug), [slug]);

  if (state.data === null) return <PublicState state={state} what={t('publicPortal.results')} />;
  return (
    <>
      <PublicResultsView data={state.data} slug={slug} base={base} onNavigate={onNavigate} />
      <Updated updatedAt={state.updatedAt} error={state.error} />
    </>
  );
}

export function PublicResultsView({
  data,
  slug,
  base,
  onNavigate,
}: {
  data: PublicResultsResponse;
  slug: string;
  base: string;
  onNavigate: (href: string) => void;
}): JSX.Element {
  const t = useT();
  const [competitionId, setCompetitionId] = useState('');

  const entries =
    competitionId === ''
      ? data.entries
      : data.entries.filter((e) => e.competitionId === competitionId);

  // Agrupado por día, conservando el orden que ya trae el server.
  const days: Array<{ key: string; label: string; entries: PublicFixtureEntry[] }> = [];
  for (const entry of entries) {
    const key = localDayKey(entry.scheduledAt);
    const last = days[days.length - 1];
    if (last && last.key === key) last.entries.push(entry);
    else days.push({ key, label: localDayLabel(entry.scheduledAt, t('publicPortal.noDate')), entries: [entry] });
  }

  return (
    <>
      <h1 className="pub-title">{t('publicPortal.results')}</h1>
      <p className="pub-sub">{t('publicPortal.resultsHint')}</p>

      {data.competitions.length > 1 ? (
        <p style={{ marginBottom: 16 }}>
          <label>
            {t('publicPortal.filterCompetition')}{' '}
            <select value={competitionId} onChange={(e) => setCompetitionId(e.target.value)}>
              <option value="">{t('publicPortal.allCompetitions')}</option>
              {data.competitions.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name} {c.season}
                </option>
              ))}
            </select>
          </label>
        </p>
      ) : null}

      {days.length === 0 ? (
        <EmptyBlock>{t('publicPortal.noResults')}</EmptyBlock>
      ) : (
        days.map((day) => (
          <Section key={day.key || day.label} title={day.label}>
            <div className="pub-grid">
              {day.entries.map((entry) => (
                <MatchCard
                  key={entry.id}
                  slug={slug}
                  base={base}
                  entry={entry}
                  onNavigate={onNavigate}
                />
              ))}
            </div>
          </Section>
        ))
      )}

      {data.truncated ? <p className="pub-note">{t('publicPortal.truncated')}</p> : null}
    </>
  );
}
